import React from 'react';
import { TicketGroupAvailabilityBehavior } from '@/dashboard/enums/tickets';
import { formatSaleDate } from '@repo/checkout/lib/formatSaleDate';

function TicketGroupSaleDate({ page, ticketGroup, allTicketGroups = [] }) {
  if (ticketGroup.availabilityStatus === 'unavailable') return null;

  const now = new Date();

  const availabilityBehavior =
    ticketGroup.availabilityBehavior || TicketGroupAvailabilityBehavior.ALWAYS_AVAILABLE;

  const dateBasedBehaviors = [
    TicketGroupAvailabilityBehavior.UNTIL_EVENT_STARTS,
    TicketGroupAvailabilityBehavior.DATE_TIME_BASED,
    TicketGroupAvailabilityBehavior.AFTER_TICKET_SOLD_OUT,
    TicketGroupAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
    TicketGroupAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
  ];
  if (!dateBasedBehaviors.includes(availabilityBehavior)) {
    return null;
  }

  return (
    <div className="text-sm text-gray-700 w-full">
      {getTicketGroupDateMessage(ticketGroup, allTicketGroups, now, page)}
    </div>
  );
}

function formatSaleDateForDashboard(startDate, endDate, page, now = new Date()) {
  const result = formatSaleDate(startDate, endDate, {
    now,
    timezone: page.eventDetails?.timezone,
    locale: page.locale,
    showTimezone: true,
    startText: 'Starts',
    startedText: 'Started on',
    endText: 'Ends',
    endedText: 'Ended on',
    showBothMessages: true,
  });

  // Convert array of messages to JSX elements
  if (Array.isArray(result)) {
    return (
      <>
        {result.map((message, index) => (
          <div key={index}>{message}</div>
        ))}
      </>
    );
  }

  return result ? <div>{result}</div> : null;
}

function getTicketGroupDateMessage(ticketGroup, allTicketGroups, now, page) {
  const availabilityBehavior =
    ticketGroup.availabilityBehavior || TicketGroupAvailabilityBehavior.ALWAYS_AVAILABLE;

  switch (availabilityBehavior) {
    case TicketGroupAvailabilityBehavior.UNTIL_EVENT_STARTS: {
      if (page && page.eventDetails && page.eventDetails.startDate) {
        const eventStartDate = new Date(page.eventDetails.startDate);
        const message = formatSaleDateForDashboard(null, eventStartDate, page, now);
        return message;
      }
      return null;
    }
    case TicketGroupAvailabilityBehavior.DATE_TIME_BASED: {
      const message = formatSaleDateForDashboard(
        ticketGroup.saleStartOn,
        ticketGroup.saleEndOn,
        page,
        now,
      );
      return message;
    }

    case TicketGroupAvailabilityBehavior.AFTER_TICKET_SOLD_OUT: {
      if (ticketGroup.triggerTicketGroup) {
        const referencedType = allTicketGroups.find(
          tt => tt._id === ticketGroup.triggerTicketGroup,
        );

        const message = formatSaleDateForDashboard(null, ticketGroup.saleEndOn, page, now);

        if (referencedType && referencedType.status === 'enabled') {
          return (
            <>
              <div>
                Starts if group <span className="font-semibold">{referencedType.name}</span> sells
                out
              </div>
              {message}
            </>
          );
        }
      }
      break;
    }

    case TicketGroupAvailabilityBehavior.AFTER_TICKET_SALE_ENDS: {
      if (ticketGroup.triggerTicketGroup) {
        const referencedType = allTicketGroups.find(
          tt => tt._id === ticketGroup.triggerTicketGroup,
        );
        if (referencedType && referencedType.status === 'enabled' && referencedType.saleEndOn) {
          const message = formatSaleDateForDashboard(
            ticketGroup.saleStartOn,
            ticketGroup.saleEndOn,
            page,
            now,
          );

          return (
            <>
              <div>
                When group <span className="font-semibold">{referencedType.name}</span> sale ends
              </div>
              {message}
            </>
          );
        }
      }
      break;
    }

    case TicketGroupAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT: {
      if (ticketGroup.triggerTicketGroup) {
        const referencedType = allTicketGroups.find(
          tt => tt._id === ticketGroup.triggerTicketGroup,
        );
        if (referencedType && referencedType.status === 'enabled') {
          const message = formatSaleDateForDashboard(
            ticketGroup.saleStartOn,
            ticketGroup.saleEndOn,
            page,
            now,
          );
          return (
            <>
              <div>
                <div>
                  Starts if group <span className="font-semibold">{referencedType.name}</span> sells
                  out{referencedType.saleEndOn != null && ' or,'}
                </div>
                {message}
              </div>
            </>
          );
        }
      }
      break;
    }
    default: {
      return null;
    }
  }
}

export default TicketGroupSaleDate;
